import * as borogove from "borogove";
import Fuse from "fuse.js";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Menu, MenuItem, Dialog, ListItemIcon } from "@mui/material";
import { QRCanvas, frontalCamera } from "qr/dom.js";
import { Virtuoso } from "react-virtuoso";
import { useDebounce } from "usehooks-ts";
import { useQueryState } from "nuqs";
import {
	useState,
	useEffect,
	useRef,
	useSyncExternalStore,
	useCallback,
	useContext,
} from "react";
import {
	Outlet,
	Navigate,
	useNavigate,
	useParams,
	useSearchParams,
	useLocation,
	useOutletContext,
} from "react-router";
import {
	Button,
	AddUserButton,
	ArrowButton,
	MainContainer,
	Sidebar,
	Conversation,
	Avatar,
	ConversationHeader,
	TypingIndicator,
	Search,
} from "@chatscope/chat-ui-kit-react";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPencil,
	faXmark,
	faMagnifyingGlassArrowRight,
	faRightFromBracket,
	faThumbtack,
	faThumbtackSlash,
} from "@fortawesome/free-solid-svg-icons";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { confirmDialog } from "./ConfirmDialog";
import { SettingsContext } from "./Settings";
import { href, showNotification, useEffectEvent, useDrafts } from "../util";

borogove.Config.relativeHashUri = true;

export const Chats = () => {
	const location = useLocation();
	const params = useParams();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [searchQ, setSearchQ] = useQueryState("q");
	const { persistence, client, logout } = useOutletContext<{
		persistence: borogove.Persistence & borogove.persistence.KeyValueStore;
		client: borogove.Client;
		logout: (completely: boolean) => void;
	}>();
	const { audibleNotifications } = useContext(SettingsContext);

	const showSidebar = !params.chatId;
	const showRightSidebar = location.pathname.endsWith("/details");

	const chatProxies = useRef(new WeakMap()).current;
	const chatsRef = useRef<borogove.Chat[] | null>(client.getChats());
	const chats: borogove.Chat[] = useSyncExternalStore(
		(callback) => {
			const token = client.addChatsUpdatedListener((updatedChats) => {
				for (const chat of updatedChats) {
					chatProxies.delete(chat);
				}

				chatsRef.current = null;
				callback();
			});

			return () => {
				client.removeEventListener(token);
			};
		},
		() => {
			if (chatsRef.current != null) return chatsRef.current;

			return (chatsRef.current = client.getChats().map((chat) => {
				if (!chatProxies.has(chat)) {
					chatProxies.set(chat, new Proxy(chat, {}));
				}
				return chatProxies.get(chat);
			}));
		},
	);

	const activeChat = params.chatId
		? chats.find((c) => c.chatId === params.chatId)
		: null;
	let activeChatNotifications: "all" | "mute" | "mention" | "reply" = "all";
	if (activeChat) {
		if (activeChat.notificationsFiltered()) {
			if (activeChat.notifyReply()) {
				activeChatNotifications = "reply";
			} else if (activeChat.notifyMention()) {
				activeChatNotifications = "mention";
			} else {
				activeChatNotifications = "mute";
			}
		}
	}

	const openNotificationsRef = useRef([] as Notification[]);
	const notificationEvent = useEffectEvent((message: borogove.ChatMessage) => {
		(async () => {
			if (message.type === borogove.MessageType.MessageCall) return;
			const chat = client.getChat(message.chatId());
			if (!chat) return;

			if (
				!document.hidden &&
				document.hasFocus() &&
				message.chatId() === activeChat?.chatId
			)
				return;
			if (
				!chat.notificationsFiltered() ||
				(chat.notifyMention() &&
					(message.text ?? "").indexOf(client.displayName()) >= 0) ||
				(chat.notifyReply() &&
					message.replyToMessage &&
					chat.getParticipantDetails(message.replyToMessage.senderId)?.isSelf)
			) {
				if (audibleNotifications) new Audio("/message.opus").play();
				const notif = await showNotification(client.accountId(), message);
				if (notif) openNotificationsRef.current.push(notif);
			}
		})();
	});

	const [typingUsers, setTypingUsers] = useState<Record<string, string[]>>({});
	useEffect(() => {
		chatsRef.current = null;
		const userStateToken = client.addUserStateListener(
			(userId, chatId, threadId, state) => {
				void threadId;

				if (state === borogove.UserState.Composing) {
					setTypingUsers((existing) => ({
						...existing,
						[chatId]: [...(existing[chatId] || []), userId],
					}));
				} else {
					setTypingUsers((existing) => ({
						...existing,
						[chatId]: (existing[chatId] || []).filter((id) => id !== userId),
					}));
				}
			},
		);

		const messageToken = client.addChatMessageListener(
			(message: borogove.ChatMessage) => {
				if (message.isIncoming()) notificationEvent(message);
			},
		);

		return () => {
			client.removeEventListener(userStateToken);
			client.removeEventListener(messageToken);
		};
	}, [client]);

	const { getDraft, setDraft } = useDrafts();

	// Request permissions once the user is logged in and seeing the app
	useEffect(() => {
		if (navigator.storage && navigator.storage.persist) {
			navigator.storage.persist();
		}

		(async () => {
			if (Notification.requestPermission) {
				await Notification.requestPermission();
			}
		})();
	}, []);

	useEffect(() => {
		const clickHandler = (e: MouseEvent) => {
			const target =
				e.target instanceof HTMLElement ? e.target.closest("a") : null;

			if (target && target.href.startsWith("xmpp:")) {
				e.preventDefault();
				if (screen.width < 576) {
					navigate(
						href(
							"/accounts/:accountId/chats",
							{
								accountId: client.accountId(),
							},
							new URLSearchParams({ q: target.href }),
						),
					);
				} else {
					setSearchQ(target.href);
				}
			}
		};
		document.addEventListener("click", clickHandler);

		return () => {
			document.removeEventListener("click", clickHandler);
		};
	}, [setSearchQ, client, navigate]);

	const debouncedSearch = useDebounce(searchQ, 300);
	const [availableChats, setAvailableChats] = useState({
		q: "",
		chats: [] as borogove.AvailableChat[],
	});
	const [addContactAnchorEl, setAddContactAnchorEl] =
		useState<null | HTMLElement>(null);

	const [scanQRCode, setScanQRCode] = useState(false);
	const [qrStatus, setQRStatus] = useState("Loading camera...");
	const qrDecodeTimeout = useRef<number | null>(null);
	const qrCamera = useRef<Awaited<ReturnType<typeof frontalCamera>> | null>(
		null,
	);
	const qrScanVideo = useCallback(
		(video: HTMLVideoElement) => {
			if (qrDecodeTimeout.current) {
				clearTimeout(qrDecodeTimeout.current);
				qrDecodeTimeout.current = null;
			}
			if (qrCamera.current) {
				qrCamera.current.stop();
				qrCamera.current = null;
			}
			if (scanQRCode && video) {
				const canvas = new QRCanvas({}, { cropToSquare: false });
				frontalCamera(video).then((camera) => {
					qrCamera.current = camera;
					const checkForQR = () => {
						const data = camera.readFrame(canvas);
						if (data) {
							if (data.startsWith("xmpp:")) {
								setSearchQ(data);
								setScanQRCode(false);
							} else {
								setQRStatus("Invalid QR Code found: " + data);
							}
						} else {
							setQRStatus("No QR Code found");
						}
						qrDecodeTimeout.current = setTimeout(() => {
							checkForQR();
						}, 100);
					};
					checkForQR();
				});
			}
		},
		[scanQRCode, setSearchQ],
	);

	const confirmThenLogout = () => {
		confirmDialog({
			message:
				"Do you want to also delete all chats and messages from this device?",
			accept: () => logout(true),
			reject: () => logout(false),
		});
	};

	const startChat = (availableChat: borogove.AvailableChat, trust: boolean) => {
		const chat = client.startChat(availableChat);
		if (trust) chat.setTrusted(trust);
		chat.bookmark();
		return chat;
	};

	const conversationInfo = (c: borogove.Chat) => {
		let info: string | JSX.Element = c.preview();
		const draft = getDraft(c.chatId);
		if (c.chatId !== params.chatId && draft?.text) {
			info = "Draft: " + draft.text;
		}
		if (c.syncing()) {
			info = `Syncing (${(c as any).inSync ? "S" : "s"}${(c as any).livePresence() ? "L" : "l"}) ...`;
		}
		if (c.callStatus() === borogove.CallStatus.Incoming) {
			info = "📲 " + info;
		}
		if (c.callStatus() === borogove.CallStatus.Ongoing) {
			info = "📞 " + info;
		}
		if ((typingUsers[c.chatId] || []).length > 0) {
			info = <TypingIndicator content={info} />;
		}

		return {
			fn: c.getDisplayName(),
			photo: c.getPhoto(),
			placeholder: c.getPlaceholder(),
			info,
		};
	};

	const conversationSearch = new Fuse<borogove.Chat>(chats, {
		useExtendedSearch: true,
		keys: [
			{
				name: "fn",
				weight: 2,
				getFn: (c: borogove.Chat) => c.getDisplayName(),
			},
			"chatId",
		],
	});

	const searchChats = useEffectEvent(() => {
		const search = searchQ;
		if (search) {
			client.findAvailableChats(search, (q, chats) => {
				if (search === q) setAvailableChats({ q: q, chats: chats });
				return search !== q;
			});
		}
	});
	useEffect(() => {
		searchChats();
	}, [debouncedSearch]);

	const renderConversations: Array<
		{ item: borogove.Chat } | borogove.AvailableChat
	> = searchQ
		? (searchQ === availableChats.q
				? availableChats.chats.filter(
						(result) => !chats.find((c) => c.chatId === result.chatId),
					)
				: ([] as Array<Fuse.FuseResult<borogove.Chat> | borogove.AvailableChat>)
			).concat(conversationSearch.search(searchQ))
		: chats.map((c) => ({ item: c }));

	return (
		<MainContainer responsive>
			<Sidebar
				position="left"
				scrollable={false}
				className={showSidebar ? "active" : ""}
			>
				<ConversationHeader>
					<ConversationHeader.Content>
						<h1>Messages</h1>
					</ConversationHeader.Content>
					<ConversationHeader.Actions>
						<Button
							title="Start new chat"
							icon={<FontAwesomeIcon icon={faPencil} />}
							onClick={(e) => setAddContactAnchorEl(e.currentTarget)}
						/>
						<Menu
							open={!!addContactAnchorEl}
							anchorEl={addContactAnchorEl}
							onClose={() => setAddContactAnchorEl(null)}
						>
							<MenuItem>
								<form
									onSubmit={(e) => {
										e.preventDefault();
										const q = e.currentTarget.querySelector("input")?.value;
										setSearchQ(q || null);
										setAddContactAnchorEl(null);
									}}
								>
									<Search placeholder="Search by name, ID" />
								</form>
							</MenuItem>
							<MenuItem
								onClick={() => {
									setScanQRCode(true);
									setAddContactAnchorEl(null);
								}}
							>
								<ListItemIcon>
									<QrCodeScannerIcon />
								</ListItemIcon>
								Scan QR Code
							</MenuItem>
						</Menu>
						<Button
							title="Remove this account"
							icon={<FontAwesomeIcon icon={faRightFromBracket} />}
							onClick={confirmThenLogout}
						/>
					</ConversationHeader.Actions>
				</ConversationHeader>
				<Search
					placeholder="Search by name, ID"
					value={searchQ || ""}
					onChange={(q) => setSearchQ(q || null)}
					onClearClick={() => setSearchQ(null)}
				/>
				{searchQ && searchQ.length > 0 && (
					<Conversation
						name={"Search messages for"}
						info={searchQ}
						onClick={() => {
							navigate(
								href(
									"/accounts/:accountId/search",
									{
										accountId: client.accountId(),
									},
									searchParams,
								),
							);
						}}
					>
						<Conversation.Operations visible>
							<Button
								icon={<FontAwesomeIcon icon={faMagnifyingGlassArrowRight} />}
								onClick={() => {
									navigate(
										href(
											"/accounts/:accountId/search",
											{
												accountId: client.accountId(),
											},
											searchParams,
										),
									);
								}}
							/>
						</Conversation.Operations>
					</Conversation>
				)}
				<Virtuoso
					totalCount={renderConversations.length}
					itemContent={(index) => {
						const result = renderConversations[index];
						if (!("item" in result)) {
							return (
								<Conversation
									key={result.chatId}
									name={result.displayName}
									info={result.note}
									onClick={() =>
										navigate(
											href(
												"./chats/:chatId",
												{
													chatId: startChat(result, false)?.chatId,
												},
												searchParams,
											),
										)
									}
								>
									<Conversation.Operations visible>
										{!result.isChannel() && (
											<AddUserButton
												onClick={() =>
													navigate(
														href(
															"./chats/:chatId",
															{
																chatId: startChat(result, true)?.chatId,
															},
															searchParams,
														),
													)
												}
											/>
										)}
										{result.isChannel() && (
											<ArrowButton
												direction="right"
												onClick={() =>
													navigate(
														href(
															"./chats/:chatId",
															{
																chatId: startChat(result, true)?.chatId,
															},
															searchParams,
														),
													)
												}
											/>
										)}
									</Conversation.Operations>
								</Conversation>
							);
						}

						const c: borogove.Chat = result.item;

						// Helper for getting the data of the first participant
						const info = conversationInfo(c);

						return (
							<Conversation
								key={c.chatId}
								name={info.fn}
								info={info.info}
								active={params.chatId === c.chatId}
								unreadCnt={c.unreadCount()}
								accessKey={index < 10 ? "" + (index + 1) : undefined}
								onClick={() => {
									const scrollTo = getDraft(c.chatId)?.scrollTo;
									navigate(
										href(
											"./chats/:chatId",
											{ chatId: c.chatId },
											searchParams,
										) +
											(scrollTo || c.readUpTo()
												? `#message:${scrollTo || c.readUpTo()}`
												: ""),
									);
								}}
							>
								<AvatarWithPlaceholder
									as={Avatar}
									photoUri={info.photo}
									placeholderUri={info.placeholder}
									displayName={info.fn}
								/>
							</Conversation>
						);
					}}
				/>
			</Sidebar>

			{params.chatId && !activeChat ? (
				<Navigate
					to={href("/accounts/:accountId", { accountId: client.accountId() })}
				/>
			) : (
				<Outlet
					context={{
						active: !showSidebar && !showRightSidebar,
						activeChat,
						persistence,
						getDraft,
						setDraft,
						client,
						openNotificationsRef,
						typingUsers: params.chatId ? typingUsers[params.chatId] || [] : [],
					}}
				/>
			)}

			{activeChat && (
				<Sidebar position="right" className={showRightSidebar ? "active" : ""}>
					<Button
						icon={<FontAwesomeIcon icon={faXmark} />}
						onClick={() =>
							navigate(location.pathname.replace(/\/details$/, ""))
						}
						style={{ float: "right" }}
					/>
					<h2>Chat details</h2>
					<code>{activeChat.chatId}</code>
					{activeChat instanceof borogove.Channel && (
						<p className="description">{activeChat.description()}</p>
					)}

					<section>
						<h3>Settings</h3>
						<Button
							icon={
								<FontAwesomeIcon
									icon={
										activeChat.uiState === borogove.UiState.Pinned
											? faThumbtackSlash
											: faThumbtack
									}
								/>
							}
							onClick={() => activeChat.togglePinned()}
						>
							{activeChat.uiState === borogove.UiState.Pinned
								? "Unpin Chat"
								: "Pin Chat"}
						</Button>
						<Button onClick={() => activeChat.close()}>
							{activeChat.getParticipants().length > 2
								? "Leave chat"
								: "Archive chat"}
						</Button>

						<label>
							<span>Notifications</span>
							<select
								onChange={(e) => {
									if (e.target.value === "all") {
										activeChat.setNotifications(false, true, true);
									} else if (e.target.value === "mute") {
										activeChat.setNotifications(true, false, false);
									} else if (e.target.value === "mention") {
										activeChat.setNotifications(true, true, false);
									} else if (e.target.value === "reply") {
										activeChat.setNotifications(true, true, true);
									}
								}}
							>
								<option
									value="all"
									selected={activeChatNotifications === "all"}
								>
									All Messages
								</option>
								<option
									value="mention"
									selected={activeChatNotifications === "mention"}
								>
									Mentions Only
								</option>
								<option
									value="reply"
									selected={activeChatNotifications === "reply"}
								>
									Mentions and Replies Only
								</option>
								<option
									value="mute"
									selected={activeChatNotifications === "mute"}
								>
									Mute
								</option>
							</select>
						</label>

						{activeChat.isBlocked && (
							<Button onClick={() => activeChat.unblock(true)}>Unblock</Button>
						)}
						{!activeChat.isBlocked && (
							<Button onClick={() => activeChat.block(null, true)}>
								Block
							</Button>
						)}
						{!activeChat.isBlocked && (
							<Button
								onClick={() => activeChat.block(activeChat.lastMessage, true)}
							>
								Report Spam
							</Button>
						)}

						{activeChat.getParticipants().length > 2 && (
							<>
								<h3>Members</h3>
								<ul>
									{activeChat
										.getParticipants()
										.sort()
										.map((p) => (
											<li key={`participant-${p}`}>
												{activeChat.getParticipantDetails(p).displayName}
											</li>
										))}
								</ul>
							</>
						)}
					</section>
				</Sidebar>
			)}

			<Dialog
				open={scanQRCode}
				onClose={() => setScanQRCode(false)}
				fullScreen={useMediaQuery("(max-width: 576px)")}
				onClick={() => setScanQRCode(false)}
			>
				<video ref={qrScanVideo}></video>
				<p style={{ textAlign: "center", marginTop: "1em" }}>{qrStatus}</p>
			</Dialog>
		</MainContainer>
	);
};
